import type {
    CompRef,
    DalValue,
    DependenciesBuilder,
    DependenciesManager,
    GlobalData,
    Namespace,
    Pointer
} from '@wix/document-services-types'
import type {CreateExtArgs} from '@wix/document-manager-core'
import {getResolvedDataItemsByNamespaceMap, updateDataItemsByNamespacesDataMap} from './dataItems'
import {DATA_TYPES, MASTER_PAGE_ID} from '../../../../../constants/constants'
import {DataNodesWhitelist, EXCLUDED_NAMESPACES, GLOBAL_NAMESPACES} from '../../constants'
import _ from 'lodash'
import type {DataModelExtensionAPI} from '../../../../dataModel/dataModel'

const getPermanentDataNodesPointers = ({pointers}: CreateExtArgs): Pointer[] => {
    const permanentDataNodePointers: Pointer[] = []
    for (const namespace of Object.values(DATA_TYPES)) {
        permanentDataNodePointers.push(...pointers.data.getPermanentNodes(namespace))
    }

    return permanentDataNodePointers
}

const getGlobalNamespacesDataNodesPointers = ({dal, pointers}: CreateExtArgs): Pointer[] =>
    _.flatMap(GLOBAL_NAMESPACES, (dataNodesWhitelist: DataNodesWhitelist, namespace: Namespace) => {
        if (dataNodesWhitelist === 'All') {
            return dal.getAllItemsInNamespace(namespace)
        }

        const globalNamespaceDataNodesPointers: Pointer[] = []
        dataNodesWhitelist.forEach((id: string) => {
            const pointer = pointers.getPointer(id, namespace)
            if (dal.has(pointer)) {
                globalNamespaceDataNodesPointers.push(pointer)
            }
        })

        return globalNamespaceDataNodesPointers
    })

const getPermanentDataNodes = (
    extensionContext: CreateExtArgs,
    componentPointer: CompRef,
    dependenciesBuilder: DependenciesBuilder
): GlobalData | undefined => {
    const permanentDataNodePointers = getPermanentDataNodesPointers(extensionContext)
    const filteredPermanentDataNodePointers = permanentDataNodePointers.filter(
        (dataNodePointer: Pointer) =>
            !dependenciesBuilder.references.hasInternalRef(dataNodePointer.id, dataNodePointer.type) &&
            !EXCLUDED_NAMESPACES.has(dataNodePointer.type)
    )

    return getResolvedDataItemsByNamespaceMap(
        extensionContext,
        filteredPermanentDataNodePointers,
        componentPointer,
        dependenciesBuilder
    )
}

const getGlobalNamespacesDataNodes = (extensionContext: CreateExtArgs): GlobalData | undefined => {
    const {extensionAPI} = extensionContext
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    const globalNamespacesDataNodesPointers = getGlobalNamespacesDataNodesPointers(extensionContext)
    if (globalNamespacesDataNodesPointers.length === 0) {
        return
    }

    const globalNamespaceDataNodes = {}
    for (const pointer of globalNamespacesDataNodesPointers) {
        const {id, type} = pointer
        const dataItem = dataModel.getItem(id, type, MASTER_PAGE_ID, undefined, undefined, true)
        globalNamespaceDataNodes[type] ??= {}
        globalNamespaceDataNodes[type][id] = dataItem
    }

    return globalNamespaceDataNodes
}

export const getGlobalData = (
    extensionContext: CreateExtArgs,
    componentPointer: CompRef,
    dependenciesBuilder: DependenciesBuilder
): GlobalData | undefined => {
    const {pointers} = extensionContext
    if (!pointers.structure.isMasterPage(componentPointer)) {
        return
    }

    const permanentDataNodes = getPermanentDataNodes(extensionContext, componentPointer, dependenciesBuilder)
    const globalNamespacesDataNodes = getGlobalNamespacesDataNodes(extensionContext)

    if (permanentDataNodes === undefined && globalNamespacesDataNodes === undefined) {
        return
    }

    return {
        ...permanentDataNodes,
        ...globalNamespacesDataNodes
    }
}

const splitGlobalData = (
    globalData?: GlobalData
): {permanentDataNodes: GlobalData; globalNamespacesDataNodes: GlobalData} => {
    const permanentDataNodes: GlobalData = {}
    const globalNamespacesDataNodes: GlobalData = {}

    if (globalData) {
        for (const [namespace, items] of Object.entries(globalData)) {
            const relevantGroup = GLOBAL_NAMESPACES[namespace] ? globalNamespacesDataNodes : permanentDataNodes
            relevantGroup[namespace] = items
        }
    }

    return {permanentDataNodes, globalNamespacesDataNodes}
}

const updatePermanentDataNodes = (
    extensionContext: CreateExtArgs,
    componentPointer: CompRef,
    permanentDataNodes: GlobalData,
    dependenciesManager: DependenciesManager
) => {
    const origPermanentDataNodesPointers = getPermanentDataNodesPointers(extensionContext)
    updateDataItemsByNamespacesDataMap(
        extensionContext,
        origPermanentDataNodesPointers,
        permanentDataNodes,
        componentPointer,
        dependenciesManager
    )
}

const updateGlobalNamespaces = (extensionContext: CreateExtArgs, globalNamespacesDataNodes: GlobalData) => {
    const {dal, pointers} = extensionContext

    _.forOwn(globalNamespacesDataNodes, (items: Record<string, DalValue>, namespace: string) => {
        _.forOwn(items, (dataItem: DalValue, id: string) => {
            const pointer = pointers.getPointer(id, namespace)
            dal.set(pointer, dataItem)
        })
    })

    const origGlobalNamespacesDataNodesPointers = getGlobalNamespacesDataNodesPointers(extensionContext)
    for (const pointer of origGlobalNamespacesDataNodesPointers) {
        if (globalNamespacesDataNodes[pointer.type]?.[pointer.id] === undefined) {
            dal.remove(pointer)
        }
    }
}

export const updateGlobalData = (
    extensionContext: CreateExtArgs,
    componentPointer: CompRef,
    globalData: GlobalData | undefined,
    dependenciesManager: DependenciesManager
) => {
    const {pointers} = extensionContext
    if (!pointers.structure.isMasterPage(componentPointer)) {
        return
    }

    const {permanentDataNodes, globalNamespacesDataNodes} = splitGlobalData(globalData)

    updatePermanentDataNodes(extensionContext, componentPointer, permanentDataNodes, dependenciesManager)
    updateGlobalNamespaces(extensionContext, globalNamespacesDataNodes)
}
